import { useRef, useState } from 'react';
import { AlertTriangle, ShieldCheck } from 'lucide-react';

const ungoverned = [
  'Vague prompts, unclear outcome',
  'No record of what changed',
  'Confidence without proof',
  'Accountability is ambiguous',
];

const governed = [
  'Clear intent stated up front',
  'Every change carries evidence',
  'Proof before completion',
  'A human stays accountable',
];

/**
 * Interactive "quality in, quality out" comparator. Drag the handle to reveal
 * the difference between ungoverned AI activity and governed AI work.
 */
export function QualityComparator() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState(52); // percent revealed of the governed side
  const dragging = useRef(false);

  const setFromClientX = (clientX: number) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const frac = Math.min(96, Math.max(4, ((clientX - rect.left) / rect.width) * 100));
    setPos(frac);
  };

  return (
    <div className="w-full">
      <div
        ref={ref}
        className="relative h-72 w-full select-none overflow-hidden rounded-2xl border border-[#074EDC]/20 md:h-64"
        onMouseMove={(e) => dragging.current && setFromClientX(e.clientX)}
        onMouseUp={() => (dragging.current = false)}
        onMouseLeave={() => (dragging.current = false)}
        onTouchMove={(e) => dragging.current && setFromClientX(e.touches[0].clientX)}
        onTouchEnd={() => (dragging.current = false)}
      >
        {/* ungoverned (base layer) */}
        <div className="absolute inset-0 bg-gradient-to-br from-red-950/70 to-slate-900/90 p-6">
          <div className="mb-4 flex items-center gap-2 text-white/40">
            <AlertTriangle className="h-5 w-5 text-white/30" />
            <span>Ungoverned AI activity</span>
          </div>
          <ul className="space-y-2">
            {ungoverned.map((t) => (
              <li key={t} className="flex items-start gap-2 text-white/35">
                <span className="mt-2 h-1 w-1 flex-shrink-0 rounded-full bg-slate-500" />
                {t}
              </li>
            ))}
          </ul>
        </div>

        {/* governed (revealed layer) */}
        <div
          className="absolute inset-0 overflow-hidden bg-gradient-to-br from-green-950/60 to-slate-900/80 p-6"
          style={{ clipPath: `inset(0 0 0 ${pos}%)` }}
        >
          <div className="mb-4 flex items-center justify-end gap-2 text-[#074EDC]">
            <span>Governed AI work</span>
            <ShieldCheck className="h-5 w-5 text-[#4AAC00]" />
          </div>
          <ul className="space-y-2 text-right">
            {governed.map((t) => (
              <li key={t} className="flex items-start justify-end gap-2 text-white/80">
                {t}
                <span className="mt-2 h-1 w-1 flex-shrink-0 rounded-full bg-[#4AAC00]" />
              </li>
            ))}
          </ul>
        </div>

        {/* handle */}
        <div className="absolute inset-y-0 pointer-events-none" style={{ left: `${pos}%` }}>
          <div className="absolute inset-y-0 left-0 w-px -translate-x-1/2 bg-[#074EDC]/70" />
          <button
            type="button"
            aria-label="Drag to compare"
            onMouseDown={(e) => {
              dragging.current = true;
              setFromClientX(e.clientX);
            }}
            onTouchStart={(e) => {
              dragging.current = true;
              setFromClientX(e.touches[0].clientX);
            }}
            className="pointer-events-auto absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize rounded-full border border-blue-400/70 bg-slate-800 p-2 shadow-[0_0_24px_-6px_rgba(7,78,220,0.7)] transition-transform duration-150 hover:scale-110"
          >
            <span className="flex items-center gap-0.5 text-[#074EDC]">
              <span className="h-3 w-px bg-[#074EDC]" />
              <span className="h-4 w-px bg-[#074EDC]" />
              <span className="h-3 w-px bg-[#074EDC]" />
            </span>
          </button>
        </div>
      </div>
      <p className="mt-3 text-center text-white/55 text-sm">Drag to compare AI activity with accountable AI work.</p>
    </div>
  );
}
